/**
 * One approved revision's data, as the editor step that owns it would show it.
 *
 * Every step reads from the revision's stored programme rather than the live
 * store: these are approved documents, and nothing here can be edited.
 *
 * @module components/portfolio/comparison/RevisionStepContent
 */

import React from "react";

import {
  ModuleList,
  StructuredValue,
  ValueList,
} from "@/components/portfolio/comparison/side-by-side-values";
import { recordedCreditsLabel } from "@/components/shared/credits";
import { humanizeIdentifier } from "@/components/shared/diff-labels";
import { countLabel, KeyValue, MutedText, Text } from "@/components/ui";
import type { Programme } from "@/types";
import { creditSystemName, isFeAwardType } from "@/utils/award-types";

export interface ComparisonStep {
  id: string;
  label: string;
}

/** The editor steps a revision can be read through, in editor order. */
export const COMPARISON_STEPS: ComparisonStep[] = [
  { id: "identity", label: "Identity" },
  { id: "outcomes", label: "Programme Learning Outcomes" },
  { id: "versions", label: "Programme Versions" },
  { id: "structure", label: "Credits and Modules" },
  { id: "cas-picker", label: "CAS Modules" },
  { id: "stages", label: "Programme structure" },
  { id: "award-pathway", label: "Award Pathway" },
  { id: "electives", label: "Electives" },
  { id: "mimlos", label: "Module Learning Outcomes" },
  { id: "effort-hours", label: "Effort Hours" },
  { id: "assessments", label: "Assessments" },
  { id: "reading-lists", label: "Reading Lists" },
  { id: "mapping", label: "Mapping" },
  { id: "narrative", label: "Narrative" },
];

/** The award's own credit volume: stored FET credits default to zero, so zero is unset. */
function creditVolume(programme: Programme): string {
  const fe = Number(programme.feCredits ?? 0);
  const credits = Number(
    isFeAwardType(programme.awardType ?? "") && fe > 0 ? fe : (programme.totalCredits ?? 0),
  );
  return credits > 0 ? `${credits} ${creditSystemName(programme.awardType, credits)}` : "Not set";
}

function IdentityContent({ programme }: { programme: Programme }): React.JSX.Element {
  const fields = [
    ["Programme title", programme.title],
    ["Programme code", programme.programmeCode],
    ["Award", programme.awardType],
    ["NFQ level", programme.nfqLevel],
    ["Credit volume", creditVolume(programme)],
    ["Discipline", programme.discipline],
  ] as const;
  return (
    <KeyValue
      valueWeight="prose"
      entries={fields.map(([label, value]) => ({
        label,
        value: <StructuredValue value={value} />,
      }))}
    />
  );
}

function MappingContent({ programme }: { programme: Programme }): React.JSX.Element {
  const modules = programme.modules ?? [];
  const programmeLinks = Object.values(programme.ploToMimlos ?? {}).reduce(
    (total, ids) => total + ids.length,
    0,
  );
  const assessmentLinks = modules.reduce(
    (total, module) =>
      total +
      (module.assessments ?? []).reduce(
        (sum, assessment) => sum + (assessment.mimloIds ?? []).length,
        0,
      ),
    0,
  );
  const standardLinks = (programme.plos ?? []).reduce(
    (total, outcome) => total + (outcome.standardMappings ?? []).length,
    0,
  );
  return (
    <ul className="divide-y divide-base-300 border-y border-base-300">
      {[
        ["Award standard to PLO", standardLinks],
        ["PLO to MLO", programmeLinks],
        ["MLO to assessment", assessmentLinks],
      ].map(([label, value]) => (
        <li key={label} className="flex items-center justify-between gap-4 py-3">
          <MutedText as="span">{label}</MutedText>
          <Text as="span" size="lg" className="font-semibold">
            {value}
          </Text>
        </li>
      ))}
    </ul>
  );
}

/** The body of one step for one revision. */
export function StepContent({
  programme,
  step,
}: {
  programme: Programme;
  step: string;
}): React.ReactElement {
  const modules = programme.modules ?? [];
  if (step === "identity") {
    return <IdentityContent programme={programme} />;
  }
  if (step === "outcomes") {
    return (
      <ValueList
        items={(programme.plos ?? []).map((outcome) => ({
          id: outcome.id,
          title: outcome.text,
          detail: outcome.code ?? "No outcome code",
        }))}
        empty="No programme learning outcomes recorded."
      />
    );
  }
  if (step === "versions") {
    return (
      <ValueList
        items={(programme.versions ?? []).map((version) => ({
          id: version.id,
          title: version.label,
          detail: [version.code, version.deliveryModality, version.intakes?.join(", ")]
            .filter(Boolean)
            .join(" · "),
        }))}
        empty="No programme versions recorded."
      />
    );
  }
  if (step === "structure") {
    return (
      <ModuleList modules={modules} detail={(module) => recordedCreditsLabel(module.credits)} />
    );
  }
  if (step === "cas-picker") {
    return (
      <>
        <Text className="mb-4">
          <strong>Major award:</strong> {programme.casMajorAwardCode ?? "Not set"}
        </Text>
        <ModuleList
          modules={modules.filter((module) => module.casSource === true)}
          detail={(module) => module.casComponentCode ?? "No CAS component code"}
        />
      </>
    );
  }
  if (step === "stages") {
    return (
      <ValueList
        items={(programme.versions ?? []).flatMap((version) =>
          (version.stages ?? []).map((stage) => {
            const credits = stage.creditsTarget ?? 0;
            const held = (stage.modules ?? []).length || (stage.moduleIds ?? []).length;
            return {
              id: `${version.id}:${stage.id}`,
              title: `${version.label}: ${stage.name ?? `Stage ${stage.academicStage ?? stage.sequence ?? "unlabelled"}`}`,
              detail: `${countLabel(credits, "target credit")} · ${countLabel(held, "module")}`,
            };
          }),
        )}
        empty="No stage structure recorded."
      />
    );
  }
  if (step === "award-pathway") {
    return (
      <ValueList
        items={(programme.embeddedAwards ?? []).map((award) => ({
          id: award.id,
          title: award.title,
          detail: `${award.kind} · NFQ ${award.nfqLevel ?? "not set"} · ${recordedCreditsLabel(award.credits)}`,
        }))}
        empty="No embedded or exit awards recorded."
      />
    );
  }
  if (step === "electives") {
    return (
      <ValueList
        items={(programme.electiveDefinitions ?? []).map((definition) => ({
          id: definition.id,
          title: definition.name,
          detail: `${recordedCreditsLabel(definition.credits)} · ${countLabel(definition.groups.length, "choice option")}`,
        }))}
        empty="No elective choice slots recorded."
      />
    );
  }
  if (step === "mimlos") {
    return (
      <ValueList
        items={modules.flatMap((module) =>
          (module.mimlos ?? []).map((outcome) => ({
            id: `${module.id}:${outcome.id}`,
            title: outcome.text,
            detail: `${module.code}: ${module.title}`,
          })),
        )}
        empty="No module learning outcomes recorded."
      />
    );
  }
  if (step === "effort-hours") {
    return (
      <ModuleList
        modules={modules}
        detail={(module) => {
          const patterns = Object.keys(module.effortHours ?? {}).length;
          return `${patterns} delivery pattern${patterns === 1 ? "" : "s"}`;
        }}
      />
    );
  }
  if (step === "assessments") {
    return (
      <ValueList
        items={modules.flatMap((module) =>
          (module.assessments ?? []).map((assessment) => ({
            id: `${module.id}:${assessment.id}`,
            title: assessment.title ?? assessment.type,
            detail: `${module.code}: ${module.title} · ${
              (assessment.weighting ?? assessment.weight)
                ? `${assessment.weighting ?? assessment.weight}% weighting`
                : "Weighting not set"
            }`,
          })),
        )}
        empty="No assessments recorded."
      />
    );
  }
  if (step === "reading-lists") {
    return (
      <ValueList
        items={modules.flatMap((module) =>
          (module.readingList ?? []).map((item) => ({
            id: `${module.id}:${item.id}`,
            title: item.title ?? item.citation ?? "Untitled reading",
            detail: `${module.code}: ${module.title}${item.author ? ` · ${item.author}` : ""}`,
          })),
        )}
        empty="No reading-list items recorded."
      />
    );
  }
  if (step === "mapping") {
    return <MappingContent programme={programme} />;
  }
  const narrativeEntries = Object.entries(programme.heDescriptor ?? {});
  if (narrativeEntries.length === 0) {
    return <MutedText className="py-6">No narrative fields recorded.</MutedText>;
  }
  return (
    <KeyValue
      valueWeight="prose"
      entries={narrativeEntries.map(([field, value]) => ({
        label: humanizeIdentifier(field),
        value: <StructuredValue value={value} />,
      }))}
    />
  );
}
